// Public shop pages and seller search (backend: /shops/* and /sellers/*).
// Anonymous reads; Follow and "Report this shop" need the shopper's token.
import { API_BASE, authHeaders, handleResponse } from "./api";

// A shop that changed its handle answers 301 to the new one. fetch follows
// it, so the profile that comes back carries the CURRENT handle; the page
// compares it with the URL and replaces the URL (ShopPage.jsx).
//
// GET /shops/{handle} (2026-10-18) is the shop page's one call: hero, stats,
// review summary, and two per-viewer answers the server works out from the
// token it is sent: is_owner and is_following. The page sends the seller
// session's token when there is one (is_owner), and the shopper's token
// otherwise (is_following). Someone signed in as both who is NOT this shop's
// owner gets a second call with the shopper's token, so Follow is right too.
// Release-order safety: a backend without GET /shops (anything before the
// shop-look release) answers 404 for every shop. Then the older public
// profile is used, with no reviews, no followers and no owner controls, so
// a shop page never says "Shop not found" for a shop that exists.
const HERO_DEFAULTS = {
  avatar_small_url: null, banner_small_url: null, banner_focus_y: 50, category: null, followers_count: 0,
  reviews: { count: 0, average: null, by_star: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }, latest: [] },
  is_owner: false, is_following: false,
};

async function getShop(handle, token) {
  const response = await fetch(`${API_BASE}/shops/${encodeURIComponent(handle)}`, { headers: authHeaders(token) });
  if (response.status === 410) return { unavailable: true };
  if (response.status === 404) {
    const old = await fetch(`${API_BASE}/sellers/${encodeURIComponent(handle)}`);
    if (old.status === 410) return { unavailable: true };
    if (!old.ok) return { notFound: true };
    return { shop: { ...HERO_DEFAULTS, ...(await handleResponse(old)) } };
  }
  return { shop: await handleResponse(response) };
}

export async function fetchShop(handle, { userToken = null, sellerToken = null } = {}) {
  if (!sellerToken) return getShop(handle, userToken);
  const asSeller = await getShop(handle, sellerToken);
  if (!asSeller.shop || asSeller.shop.is_owner || !userToken) return asSeller;
  return getShop(handle, userToken);
}

export async function fetchShopReviews(handle, offset = 0) {
  const response = await fetch(`${API_BASE}/shops/${encodeURIComponent(handle)}/reviews${offset ? `?offset=${offset}` : ""}`);
  return handleResponse(response);
}

export async function setFollowing(handle, follow, token) {
  const response = await fetch(`${API_BASE}/shops/${encodeURIComponent(handle)}/follow`, {
    method: follow ? "POST" : "DELETE",
    headers: authHeaders(token),
  });
  return handleResponse(response);
}

// ── The seller's own shop (seller panel "Shop look"). Seller token only. ──

export async function uploadShopImage(kind, file, sellerToken) {
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`${API_BASE}/seller/shop/${kind}`, { method: "POST", headers: authHeaders(sellerToken), body: form });
  return handleResponse(response);
}

export async function removeShopImage(kind, sellerToken) {
  const response = await fetch(`${API_BASE}/seller/shop/${kind}`, { method: "DELETE", headers: authHeaders(sellerToken) });
  return handleResponse(response);
}

export async function fetchShopProducts(handle, { q = "", category = "", sort = "newest", cursor = "" } = {}) {
  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (category) params.set("category", category);
  if (sort && sort !== "newest") params.set("sort", sort);
  if (cursor) params.set("cursor", cursor);
  const qs = params.toString();
  const response = await fetch(`${API_BASE}/sellers/${encodeURIComponent(handle)}/products${qs ? `?${qs}` : ""}`);
  return handleResponse(response);
}

export async function searchSellers(q, page = 1) {
  const response = await fetch(`${API_BASE}/sellers/search?q=${encodeURIComponent(q)}&page=${page}`);
  return handleResponse(response);
}

// Goes to Emporia's support staff as a support request, never to the seller.
export async function reportShop(handle, body, token) {
  const response = await fetch(`${API_BASE}/threads/support`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
    body: JSON.stringify({ shop_handle: handle, body }),
  });
  return handleResponse(response);
}
